import { recordFirstValue } from "../platform/analytics";
import { useEffect, useMemo, useState, } from "react";
import { catalogSeedFor } from "../content/catalog-seed";
import { createProject, createProjectWardrobe, } from "../domain/project";
import { buildRecommendationHistory, recommendCharacters } from "../domain/recommendation";
import type {
  AppData,
  CatalogMedia,
  CosplayCandidate,
  CosplayPreferences,
} from "../domain/types";
import { fetchAniListCatalog } from "../platform/anilist";
import { createAnalyticsEvent, enqueueAnalytics, } from "../platform/analytics";

import { genreOptions, SourceBadge, PageHeader, } from "../components/shared";
export function DiscoverView({ data, setData, navigate }: { data: AppData; setData: React.Dispatch<React.SetStateAction<AppData>>; navigate: (to: string) => void }) {
  const [preferences, setPreferences] = useState(data.lastPreferences);
  const [catalog, setCatalog] = useState<CatalogMedia[]>(catalogSeedFor(preferences.mediaType));
  const [status, setStatus] = useState<"seed" | "loading" | "live" | "error">("seed");
  const [message, setMessage] = useState("이미지와 정확한 원본 링크가 확인된 기본 후보를 먼저 보여드려요.");
  const history = useMemo(() => buildRecommendationHistory(data.projects, data.wardrobe), [data.projects, data.wardrobe]);
  const candidates = useMemo(() => recommendCharacters(catalog, preferences, history), [catalog, preferences, history]);
  const workCandidates = useMemo(() => {
    const groups = new Map<string, CosplayCandidate[]>();
    for (const candidate of candidates) groups.set(candidate.media.id, [...(groups.get(candidate.media.id) ?? []), candidate]);
    return [...groups.values()].sort((left, right) => right[0].tasteMatchCount - left[0].tasteMatchCount).slice(0, 6);
  }, [candidates]);

  const setPreference = <K extends keyof CosplayPreferences>(key: K, value: CosplayPreferences[K]) => {
    const next = { ...preferences, [key]: value };
    setPreferences(next);
    setData((current) => ({ ...current, lastPreferences: next }));
    if (key === "mediaType") {
      setCatalog(catalogSeedFor(value as CosplayPreferences["mediaType"]));
      setStatus("seed");
    }
  };

  const refresh = async (force = false) => {
    setStatus("loading");
    setMessage("AniList 공식 카탈로그에서 작품과 캐릭터를 확인하는 중이에요.");
    try {
      const live = await fetchAniListCatalog(preferences, { force });
      setCatalog(live);
      setStatus("live");
      setMessage(`${live.length}개 최신 작품을 비교했어요. 같은 조건은 5분간 재사용하고 응답을 대량 저장하지 않습니다.`);
    } catch (error) {
      setCatalog(catalogSeedFor(preferences.mediaType));
      setStatus("error");
      setMessage(`${error instanceof Error ? error.message : "외부 카탈로그를 불러오지 못했어요."} 이미지가 있는 확인본으로 계속할게요.`);
    }
  };

  useEffect(() => {
    let active = true;
    const timer = globalThis.setTimeout(() => {
      setStatus("loading");
      setMessage("선택한 장르의 최신 작품과 캐릭터를 자동으로 확인하는 중이에요.");
      void fetchAniListCatalog(preferences).then((live) => {
        if (!active) return;
        setCatalog(live);
        setStatus("live");
        setMessage(`${live.length}개 최신 작품을 자동으로 확인했어요. 같은 조건은 5분간 재사용합니다.`);
      }).catch((error) => {
        if (!active) return;
        setCatalog(catalogSeedFor(preferences.mediaType));
        setStatus("error");
        setMessage(`${error instanceof Error ? error.message : "외부 카탈로그를 불러오지 못했어요."} 이미지가 있는 확인본으로 계속할게요.`);
      });
    }, 250);
    return () => { active = false; globalThis.clearTimeout(timer); };
  }, [preferences.mediaType, preferences.genres.join("|")]);

  const selectCandidate = (candidate: CosplayCandidate) => {
    const project = createProject(candidate, preferences);
    const wardrobe = createProjectWardrobe(project.id);
    setData((current) => ({
      ...current,
      activeProjectId: project.id,
      projects: [project, ...current.projects],
      wardrobe: [...wardrobe, ...current.wardrobe],
      lastPreferences: preferences,
      analytics: enqueueAnalytics(enqueueAnalytics(current.analytics, createAnalyticsEvent("character_selected", { sourceStatus: candidate.media.source })), createAnalyticsEvent("project_created", { projectStatus: "planning", count: current.projects.length + 1 })),
    }));
    recordFirstValue("discover");
    navigate(`/project/${project.id}`);
  };

  return <>
    <PageHeader eyebrow="아이디어 찾기" title="좋아하는 작품에서 다음 캐릭터를 찾아봐요." description="작품 장르와 공식 캐릭터 이미지만으로 탐색합니다. 의상 난도·예산·노출처럼 이 자료로 알 수 없는 것은 점수로 만들지 않고 직접 확인할 항목으로 남겨요." aside={<SourceBadge source={status === "live" ? "AniList 최신 자료" : "해볼캐 확인본"} checkedAt={catalog[0]?.sourceCheckedAt ?? "확인일 미상"} />} />
    <section className="preference-console">
      <div className="console-step"><span className="console-number">01</span><div><strong>보고 싶은 쪽</strong><div className="segmented">{(["ANIME", "MANGA"] as const).map((type) => <button key={type} className={preferences.mediaType === type ? "selected" : ""} onClick={() => setPreference("mediaType", type)}>{type === "ANIME" ? "애니" : "만화"}</button>)}</div></div></div>
      <div className="console-step wide"><span className="console-number">02</span><div><strong>좋아하는 장르</strong><div className="chip-list">{genreOptions.map(([value, label]) => { const selected = preferences.genres.includes(value); return <button key={value} className={selected ? "chip selected" : "chip"} onClick={() => setPreference("genres", selected ? preferences.genres.filter((genre) => genre !== value) : [...preferences.genres, value].slice(-3))}>{label}</button>; })}</div></div></div>
      <button className="button primary console-action" disabled={status === "loading" || preferences.genres.length === 0} onClick={() => refresh(true)}>{status === "loading" ? "확인하는 중…" : "지금 다시 확인"}</button>
    </section>
    <div className={`status-ribbon ${status}`} role="status"><span>{status === "live" ? "최신 자료" : status === "loading" ? "확인 중" : status === "error" ? "확인본 사용" : "확인본"}</span>{message}</div>
    <section className="recommendation-key" aria-labelledby="idea-key-title"><div><p className="eyebrow">무엇을 보고 고르나요</p><h2 id="idea-key-title">확인된 작품 취향과 캐릭터 정보만 보여줘요.</h2><p>주연·조연과 유효한 캐릭터 이미지가 있는 후보만 씁니다. 옷의 버전, 실측, 예산, 제작 난도, 행사 규정은 후보를 고른 뒤 준비표에서 직접 확인합니다.</p></div><ul><li>보이는 근거: 작품 장르·연도·공식 원본</li><li>보이지 않는 정보: 의상 버전·비용·착용감</li><li>외모·인기·성공 가능성 점수 없음</li></ul></section>
    <section className="candidate-stack" aria-label="추천 작품과 캐릭터 후보">
      {workCandidates.length === 0 && <div className="empty-ticket"><h2>이 조건의 후보가 없어요.</h2><p>장르를 하나 이상 고르거나 애니·만화 형식을 바꿔보세요.</p></div>}
      {workCandidates.map((work, index) => { const candidate = work[0]; return <article className="candidate work-candidate" key={candidate.media.id}>
        <div className="candidate-visual work-cover" style={{ backgroundColor: candidate.media.color ?? undefined }}>{candidate.media.coverImage ? <img src={candidate.media.coverImage} alt={`${candidate.media.title} 공식 작품 이미지`} loading="lazy" /> : <div className="catalog-image-missing">작품 이미지 확인 중</div>}<span className="work-order">아이디어 {String(index + 1).padStart(2, "0")}</span></div>
        <div className="candidate-body"><div className="candidate-title"><div><p>{candidate.media.nativeTitle}</p><h2>{candidate.media.title}</h2></div><SourceBadge source={candidate.media.source} checkedAt={candidate.media.sourceCheckedAt} /></div>
          <div className="reason-columns"><div><strong>이 작품이 맞는 근거</strong><ul>{candidate.reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul></div><div className="tradeoffs"><strong>고르기 전 확인할 경계</strong><ul>{candidate.tradeoffs.map((reason) => <li key={reason}>{reason}</li>)}</ul></div></div>
          <div className="character-contact-sheet" aria-label={`${candidate.media.title} 캐릭터 선택`}>{work.map((choice) => <article className="character-choice" key={choice.id}>{choice.character.image || choice.media.coverImage ? <img src={choice.character.image ?? choice.media.coverImage} alt={`${choice.character.name} 공식 캐릭터 이미지`} loading="lazy" /> : <div className="catalog-image-missing">캐릭터 이미지 확인 중</div>}<div><strong>{choice.character.name}</strong>{choice.character.nativeName && <small>{choice.character.nativeName}</small>}</div><button className="button primary" onClick={() => selectCandidate(choice)}>이 캐릭터로 시작</button><a href={choice.character.siteUrl} target="_blank" rel="noreferrer" className="button text">캐릭터 원본 ↗</a></article>)}</div>
          <a href={candidate.media.siteUrl} target="_blank" rel="noreferrer" className="button text work-source-link">AniList 원본 ↗</a>
        </div>
      </article>; })}
    </section>
  </>;
}

